import { useEffect, useState, type ReactElement, type ReactNode, type SVGProps } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { Button, StatusBadge } from '@immo/ui-kit';
import {
  ADMIN_MEMBER_ACTIVITY_GROUPS,
  ADMIN_MEMBER_ACTIVITY_GROUP_BY_KIND,
  type AdminMemberActivityEvent,
  type AdminMemberActivityGroup,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { CalendarIcon, ChartIcon, FlowIcon, NoteIcon, ShieldIcon, UserIcon } from '@/components/layout/icons';
import { Section } from '@/components/detail-layout';

const NS = 'pages.admin.members.detail.activity';

type ActivityState =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly events: ReadonlyArray<AdminMemberActivityEvent> }
  | { readonly status: 'failed' }
  | { readonly status: 'hidden' };

type IconComponent = (props: SVGProps<SVGSVGElement>) => ReactElement;

const GROUP_ICON = {
  SIGN_INS: UserIcon,
  LEADS_AND_DEALS: FlowIcon,
  APPOINTMENTS: CalendarIcon,
  NOTES_AND_TASKS: NoteIcon,
  ADMIN_ACTIONS: ShieldIcon,
} satisfies Record<AdminMemberActivityGroup, IconComponent>;

const PILLS: ReadonlyArray<AdminMemberActivityGroup | null> = [null, ...ADMIN_MEMBER_ACTIVITY_GROUPS];

type Translate = (key: string, values?: Record<string, string>) => string;

const ARROW = '→';
const PERSON_SUBJECTS: ReadonlyArray<string> = ['CONTACT', 'LEAD', 'PROSPECT'];

// The message carries a plain arrow; the visible one is enlarged and the
// accessible text keeps the plain glyph. `{to}` is a fixed label, so the last
// arrow is always the message's own.
function withWideArrow(text: string): ReactNode {
  const index = text.lastIndexOf(ARROW);

  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <span aria-hidden className="text-[calc(1em+2px)]">
        {ARROW}
      </span>
      <span className="sr-only">{ARROW}</span>
      {text.slice(index + ARROW.length)}
    </>
  );
}

function describeTransition(record: string | null, from: string | null, to: string, t: Translate): ReactNode {
  if (from === null) {
    return record === null ? t('description.status-unlabelled', { to }) : t('description.status', { record, to });
  }

  return withWideArrow(
    record === null
      ? t('description.transition-unlabelled', { from, to })
      : t('description.transition', { record, from, to }),
  );
}

function describeAppointment(
  event: Extract<AdminMemberActivityEvent, { kind: 'APPOINTMENT_CREATED' }>,
  t: Translate,
  intl: IntlShape,
) {
  if (event.startsAt === null) return null;

  const when = intl.formatDate(event.startsAt, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(event.isAllDay ? {} : { hour: '2-digit', minute: '2-digit' }),
  });

  return event.title === null ? when : t('description.appointment', { title: event.title, when });
}

function describeActivity(event: AdminMemberActivityEvent, t: Translate, intl: IntlShape): ReactNode {
  switch (event.kind) {
    case 'SIGN_IN':
    case 'SIGN_IN_FAILED':
      return null;
    case 'LEAD_STATUS_CHANGED':
      return describeTransition(
        event.leadLabel,
        event.fromStatus === null ? null : t(`lead-status.${event.fromStatus}`),
        t(`lead-status.${event.toStatus}`),
        t,
      );
    case 'DEAL_PHASE_CHANGED':
      return describeTransition(
        event.dealLabel,
        t(`deal-phase.${event.fromPhase}`),
        t(`deal-phase.${event.toPhase}`),
        t,
      );
    case 'APPOINTMENT_CREATED':
      return describeAppointment(event, t, intl);
    case 'NOTE_CREATED':
      return event.subjectLabel === null ? null : t('description.note', { record: event.subjectLabel });
    case 'TASK_CREATED':
      return event.title;
    case 'CALL_LOGGED':
      if (event.subjectLabel === null) return null;

      return t(PERSON_SUBJECTS.includes(event.subjectType) ? 'description.call-with' : 'description.call-about', {
        record: event.subjectLabel,
      });
    case 'ACTED_ON_BEHALF': {
      const operator = event.operatorName ?? t('unknown-operator');

      return event.reason === null
        ? t('description.acted-on-behalf-no-reason', { operator })
        : t('description.acted-on-behalf', { operator, reason: event.reason });
    }

    case 'EDITED_BY_OPERATOR':
      return t('description.edited-by-operator', {
        operator: event.operatorName ?? t('unknown-operator'),
        area: t(`edit-area.${event.area}`),
      });
  }
}

export function MemberActivitySection({
  memberId,
  revision,
}: {
  readonly memberId: string;
  // Changes identity when the page's data re-loads; a change re-reads without the loading state.
  readonly revision: unknown;
}) {
  const t = useTranslations(NS);
  const intl = useIntl();
  const [group, setGroup] = useState<AdminMemberActivityGroup | null>(null);
  const [state, setState] = useState<ActivityState>({ status: 'loading' });
  const [activeDays, setActiveDays] = useState<number | null>(null);
  const [hasEvents, setHasEvents] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    apiClient.adminMembers.listActivity({ pathParams: { id: memberId }, query: group === null ? {} : { group } }).then(
      (result) => {
        if (!active) return;

        if (result.ok) {
          setState({ status: 'loaded', events: result.body.data });
          setActiveDays(result.body.activeDays);

          if (group === null) setHasEvents(result.body.data.length > 0);
        } else {
          setState({ status: result.status === 403 ? 'hidden' : 'failed' });
        }
      },
      () => {
        if (active) setState({ status: 'failed' });
      },
    );

    return () => {
      active = false;
    };
  }, [memberId, group, attempt, revision]);

  if (state.status === 'hidden') return null;

  const selectGroup = (next: AdminMemberActivityGroup | null) => {
    if (next === group) return;
    setState({ status: 'loading' });
    setGroup(next);
  };

  const retry = () => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  };

  return (
    <Section
      collapsible
      title={t('title')}
      icon={ChartIcon}
      subtitle={activeDays === null ? undefined : t('active-days', { n: activeDays })}
    >
      <div className="space-y-3">
        {hasEvents && (
          <div role="group" aria-label={t('filter.label')} className="flex flex-wrap gap-2">
            {PILLS.map((pill) => {
              const selected = pill === group;
              const Icon = pill === null ? null : GROUP_ICON[pill];

              return (
                <button
                  key={pill ?? 'ALL'}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => selectGroup(pill)}
                  className={`inline-flex h-7 items-center gap-1.5 rounded-sm border px-2 text-base-sm transition-colors duration-micro ease-out focus-visible:outline-none focus-visible:shadow-focus ${
                    selected
                      ? 'border-border-strong bg-surface text-text-primary'
                      : 'border-border bg-surface text-text-secondary hover:bg-hover-bg'
                  }`}
                >
                  {Icon !== null && (
                    <Icon
                      className={`h-3.5 w-3.5 shrink-0 ${selected ? 'text-text-primary' : 'text-text-tertiary'}`}
                      aria-hidden
                    />
                  )}
                  <span className="whitespace-nowrap">{t(`filter.${pill ?? 'ALL'}`)}</span>
                </button>
              );
            })}
          </div>
        )}
        {state.status === 'loading' && <p className="text-base-sm text-text-tertiary">{t('loading')}</p>}
        {state.status === 'failed' && (
          <div
            role="alert"
            className="flex items-center justify-between gap-4 rounded-md border border-neutral-200 bg-canvas px-4 py-3"
          >
            <p className="text-base-sm text-text-secondary">{t('error')}</p>
            <Button size="sm" variant="secondary" type="button" onClick={retry}>
              {t('retry')}
            </Button>
          </div>
        )}
        {state.status === 'loaded' && state.events.length === 0 && (
          <div className="rounded-md border border-dashed border-border bg-canvas px-4 py-6 text-center text-base-sm text-text-tertiary">
            {t(group === null ? 'empty' : 'empty-filtered')}
          </div>
        )}
        {state.status === 'loaded' && state.events.length > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border bg-white">
            {state.events.map((event, index) => {
              const Icon = GROUP_ICON[ADMIN_MEMBER_ACTIVITY_GROUP_BY_KIND[event.kind]];
              const failed = event.kind === 'SIGN_IN_FAILED';

              return (
                <li
                  key={`${event.occurredAt}-${event.kind}-${index}`}
                  className="grid grid-cols-[20px_190px_minmax(0,1fr)_auto] items-baseline gap-x-3 px-4 py-2 text-base-sm"
                >
                  <Icon
                    className={`h-4 w-4 self-center ${failed ? 'text-danger-600' : 'text-text-tertiary'}`}
                    aria-hidden
                  />
                  <span className={`font-medium ${failed ? 'text-danger-700' : 'text-text-primary'}`}>
                    {t(`kind.${event.kind}`)}
                  </span>
                  <span className="flex min-w-0 items-baseline gap-2 text-text-secondary">
                    <span className="min-w-0 truncate">{describeActivity(event, t, intl)}</span>
                    {event.onBehalfOf !== null && (
                      <span className="shrink-0">
                        <StatusBadge
                          tone="neutral"
                          size="sm"
                          label={t('on-behalf', { operator: event.onBehalfOf.operatorName ?? t('unknown-operator') })}
                        />
                      </span>
                    )}
                  </span>
                  <time dateTime={event.occurredAt} className="whitespace-nowrap text-text-tertiary tabular-nums">
                    {intl.formatDate(event.occurredAt, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </time>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Section>
  );
}
